import React, { useState, useEffect, useRef } from 'react'
import { Form, Row, Col, Button } from 'react-bootstrap'

function MinMaxFilterPopover({ column, params, setParams, onClose, onSetHandleApply, canEdit = true, hideApplyButton = false }) {
  let minAccessorKey, maxAccessorKey

  if (column.section_abbreviation === 'custom') {
    // For custom columns, use underscore format to match backend expectations
    // accessor_key is like "custom_column_uuid", so we need "custom_column_min_uuid" and "custom_column_max_uuid"
    const customColumnId = column.accessor_key.slice('custom_column_'.length)
    minAccessorKey = `custom_column_min_${customColumnId}`
    maxAccessorKey = `custom_column_max_${customColumnId}`
  } else {
    // Regular column handling
    const prefix = column.section_abbreviation?.toLowerCase()
    const suffix = column.accessor_key.slice(prefix.length)
    minAccessorKey = `${prefix}Min${suffix}`
    maxAccessorKey = `${prefix}Max${suffix}`
  }

  const isPercent = column.filter_type === 'min_max_percent'
  const operatorKey = `${column.accessor_key}Operator`
  const operatorValueKey = `${column.accessor_key}OperatorValue`

  const [operator, setOperator] = useState(params[operatorKey] || 'range')
  const [operatorValue, setOperatorValue] = useState(
    params[operatorValueKey] ? (isPercent ? params[operatorValueKey] * 100 : params[operatorValueKey]) : ''
  )

  const [minValue, setMinValue] = useState(params[minAccessorKey] || '')
  const [minDisplayValue, setMinDisplayValue] = useState(
    minValue ? (isPercent ? minValue * 100 : minValue) : ''
  )

  const [maxValue, setMaxValue] = useState(params[maxAccessorKey] || '')
  const [maxDisplayValue, setMaxDisplayValue] = useState(
    maxValue ? (isPercent ? maxValue * 100 : maxValue) : ''
  )

  // Track initial values to detect changes
  const initialMinValue = useRef(params[minAccessorKey] || '')
  const initialMaxValue = useRef(params[maxAccessorKey] || '')
  const initialOperator = useRef(params[operatorKey] || 'range')
  const initialOperatorValue = useRef(
    params[operatorValueKey] ? (isPercent ? params[operatorValueKey] * 100 : params[operatorValueKey]) : ''
  )

  const noInputOperators = ['is_zero', 'is_not_zero', 'is_negative', 'is_positive']
  const singleValueOperators = ['equals', 'not_equals']
  const showRangeInputs = operator === 'range'
  const showSingleInput = singleValueOperators.includes(operator)
  const showNoInputs = noInputOperators.includes(operator)

  const handleOperatorChange = (e) => {
    if (!canEdit) return
    const newOperator = e.target.value
    setOperator(newOperator)

    // Clear values when switching operators
    if (newOperator !== 'range') {
      setMinValue('')
      setMaxValue('')
      setMinDisplayValue('')
      setMaxDisplayValue('')
    }
    if (!singleValueOperators.includes(newOperator)) {
      setOperatorValue('')
    }
  }

  const handleMinChange = (e) => {
    if (!canEdit) return
    const newMinDisplayValue = e.target.value
    const newMinValue = isPercent ? newMinDisplayValue / 100 : newMinDisplayValue
    setMinDisplayValue(newMinDisplayValue)
    setMinValue(newMinValue)
  }

  const handleMaxChange = (e) => {
    if (!canEdit) return
    const newMaxDisplayValue = e.target.value
    const newMaxValue = isPercent ? newMaxDisplayValue / 100 : newMaxDisplayValue
    setMaxDisplayValue(newMaxDisplayValue)
    setMaxValue(newMaxValue)
  }

  const handleOperatorValueChange = (e) => {
    if (!canEdit) return
    setOperatorValue(e.target.value)
  }

  const buildParams = (currentParams) => {
    const newParams = { ...currentParams }

    if (operator === 'range') {
      newParams[minAccessorKey] = minValue
      newParams[maxAccessorKey] = maxValue
      delete newParams[operatorKey]
      delete newParams[operatorValueKey]
    } else {
      // Clear min/max when using an operator
      newParams[minAccessorKey] = ''
      newParams[maxAccessorKey] = ''
      newParams[operatorKey] = operator

      if (singleValueOperators.includes(operator)) {
        const rawValue = isPercent ? operatorValue / 100 : operatorValue
        newParams[operatorValueKey] = rawValue
      } else {
        delete newParams[operatorValueKey]
      }
    }

    return newParams
  }

  const handleApply = () => {
    const hasChanges = minValue !== initialMinValue.current ||
                       maxValue !== initialMaxValue.current ||
                       operator !== initialOperator.current ||
                       operatorValue !== initialOperatorValue.current

    if (hasChanges) {
      setParams((p) => buildParams(p))
    }
    onClose()
  }

  useEffect(() => {
    if (onSetHandleApply) {
      onSetHandleApply(handleApply)
    }
  }, [minValue, maxValue, operator, operatorValue]) // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-apply changes when hideApplyButton is true
  useEffect(() => {
    if (hideApplyButton) {
      setParams((p) => buildParams(p))
    }
  }, [minValue, maxValue, operator, operatorValue, hideApplyButton]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleClear = () => {
    setMinValue('')
    setMaxValue('')
    setMinDisplayValue('')
    setMaxDisplayValue('')
    setOperator('range')
    setOperatorValue('')
    setParams((p) => {
      const newParams = {
        ...p,
        [minAccessorKey]: '',
        [maxAccessorKey]: ''
      }
      delete newParams[operatorKey]
      delete newParams[operatorValueKey]
      return newParams
    })
  }

  return (
    <div>
      <div className="mb-3">
        <strong>{column.name}</strong>
      </div>

      <div className="mb-3">
        <Form.Select value={operator} onChange={handleOperatorChange} size="sm" disabled={!canEdit}>
          <option value="range">Range</option>
          <option value="equals">Equals</option>
          <option value="not_equals">Does Not Equal</option>
          <option value="is_zero">Is Zero</option>
          <option value="is_not_zero">Is Not Zero</option>
          <option value="is_negative">Is Negative</option>
          <option value="is_positive">Is Positive</option>
        </Form.Select>
      </div>

      {showRangeInputs && (
        <Row className="g-3 mb-3">
          <Col>
            <Form.Label className="small">
              Minimum {isPercent ? '(%)' : ''}
            </Form.Label>
            <Form.Control
              type="number"
              placeholder="Min"
              value={minDisplayValue}
              onChange={handleMinChange}
              step={'any'}
              size="sm"
              disabled={!canEdit}
            />
          </Col>
          <Col>
            <Form.Label className="small">
              Maximum {isPercent ? '(%)' : ''}
            </Form.Label>
            <Form.Control
              type="number"
              placeholder="Max"
              value={maxDisplayValue}
              onChange={handleMaxChange}
              step={'any'}
              size="sm"
              disabled={!canEdit}
            />
          </Col>
        </Row>
      )}

      {showSingleInput && (
        <div className="mb-3">
          <Form.Label className="small">
            Value {isPercent ? '(%)' : ''}
          </Form.Label>
          <Form.Control
            type="number"
            placeholder="Value"
            value={operatorValue}
            onChange={handleOperatorValueChange}
            step={'any'}
            size="sm"
            disabled={!canEdit}
          />
        </div>
      )}

      {!hideApplyButton && (
        <div className="d-flex justify-content-between gap-2">
          {canEdit && (
            <Button variant="light" size="sm" onClick={handleClear}>
              Clear
            </Button>
          )}
          <Button variant="primary" size="sm" onClick={canEdit ? handleApply : onClose}>
            {canEdit ? 'Apply' : 'Close'}
          </Button>
        </div>
      )}
    </div>
  )
}

export default MinMaxFilterPopover
